import React, { useState } from 'react';

const VolunteerTable = ({ volunteers, entities, onApprove, onUpdateStatus, setIsEditing }) => {
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [formData, setFormData] = useState({});

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      approved: 'badge approved',
      pending: 'badge pending',
      suspended: 'badge suspended',
      rejected: 'badge suspended'
    };
    return <span className={statusClasses[status] || 'badge'}>{status}</span>;
  };

  const handleShowDetails = (volunteer) => {
    setSelectedVolunteer(volunteer);
    setShowDetailsModal(true);
  };

  const handleApprove = (volunteer) => {
    setSelectedVolunteer(volunteer);
    setFormData({
      approved: true,
      notes: ''
    });
    setShowApprovalModal(true);
    setIsEditing(true);
  };

  const handleStatusChange = (volunteer) => {
    setSelectedVolunteer(volunteer);
    setFormData({
      volunteer_status: volunteer.volunteer_status,
      notes: volunteer.notes || ''
    });
    setShowStatusModal(true);
    setIsEditing(true);
  };


  const submitApproval = async () => {
    // Validar se a entidade foi selecionada
    if (!formData.entidade_id) {
      alert('Por favor, selecione uma entidade para o voluntário.');
      return;
    }

    try {
      // Adicionar o campo 'approved' como true para aprovação
      const approvalData = {
        ...formData,
        approved: true
      };

      await onApprove(selectedVolunteer.email, approvalData);
      setShowApprovalModal(false);
      setIsEditing(false);
      setSelectedVolunteer(null);
      setFormData({}); // Limpar o formulário
    } catch (error) {
      alert('Erro ao aprovar voluntário: ' + (error.response?.data?.message || error.message));
    }
  };

  const submitRejection = async () => {
    // Confirmar rejeição
    const confirmMessage = `Tem a certeza que deseja REJEITAR o voluntário ${selectedVolunteer?.nome}?\n\nEsta ação irá eliminar permanentemente o voluntário e todas as suas API keys do sistema.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Enviar rejeição (approved: false)
      const rejectionData = {
        approved: false,
        notes: formData.notes || 'Voluntário rejeitado'
      };

      await onApprove(selectedVolunteer.email, rejectionData);
      setShowApprovalModal(false);
      setIsEditing(false);
      setSelectedVolunteer(null);
      setFormData({}); // Limpar o formulário
    } catch (error) {
      alert('Erro ao rejeitar voluntário: ' + (error.response?.data?.message || error.message));
    }
  };

  const submitStatusUpdate = async () => {
    try {
      await onUpdateStatus(selectedVolunteer.email, formData);
      setShowStatusModal(false);
      setIsEditing(false);
      setSelectedVolunteer(null);
      setFormData({}); // Limpar o formulário
    } catch (error) {
      alert('Erro ao alterar status: ' + (error.response?.data?.message || error.message));
    }
  };


  const closeModals = () => {
    setShowApprovalModal(false);
    setShowStatusModal(false);
    setShowDetailsModal(false);
    setIsEditing(false);
    setSelectedVolunteer(null);
    setFormData({});
  };

  if (volunteers.length === 0) {
    return (
      <div className="empty-state">
        <h3>Nenhum voluntário encontrado</h3>
        <p>Ainda não há voluntários registados no sistema.</p>
      </div>
    );
  }

  return (
    <>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Status</th>
              <th>Entidade</th>
              <th>Último Login</th>
              <th>Registado</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {volunteers.map(volunteer => (
              <tr key={volunteer.email}>
                <td>{volunteer.nome}</td>
                <td>{volunteer.email}</td>
                <td>{getStatusBadge(volunteer.volunteer_status)}</td>
                <td>
                  {volunteer.entity_name || (
                    <span style={{ color: '#6c757d' }}>Não atribuído</span>
                  )}
                </td>
                <td>{formatDate(volunteer.last_login)}</td>
                <td>{formatDate(volunteer.created_at)}</td>
                <td>
                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-info btn-sm"
                      onClick={() => handleShowDetails(volunteer)}
                      title="Ver detalhes"
                      style={{ padding: '0.25rem 0.5rem' }}
                    >
                      ⓘ
                    </button>
                    {volunteer.volunteer_status === 'pending' && (
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleApprove(volunteer)}
                      >
                        Aprovar
                      </button>
                    )}
                    {volunteer.volunteer_status === 'approved' && (
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => handleStatusChange(volunteer)}
                      >
                        Status
                      </button>
                    )}
                    {volunteer.volunteer_status === 'suspended' && (
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleStatusChange(volunteer)}
                      >
                        Reativar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Volunteer Details Modal */}
      {showDetailsModal && selectedVolunteer && (
        <div className="modal">
          <div className="modal-content" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>Detalhes do Voluntário</h3>
              <button className="close-btn" onClick={closeModals}>×</button>
            </div>

            <div style={{ padding: '1rem' }}>
              {/* Foto e Informação Básica */}
              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', alignItems: 'flex-start' }}>
                {selectedVolunteer.foto_url && (
                  <div style={{ flexShrink: 0 }}>
                    <img
                      src={selectedVolunteer.foto_url}
                      alt={selectedVolunteer.nome}
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '3px solid #e0e0e0'
                      }}
                    />
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>ID</label>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '1rem' }}>#{selectedVolunteer.id}</p>
                  </div>
                </div>
              </div>

              {/* Informações Pessoais */}
              <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '1rem', marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 1rem', fontSize: '1rem', color: '#333' }}>Informações Pessoais</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>Nome</label>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '1rem' }}>{selectedVolunteer.nome || 'N/A'}</p>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>Email</label>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '1rem', wordBreak: 'break-word' }}>
                      {selectedVolunteer.email}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>Telefone</label>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '1rem' }}>{selectedVolunteer.telefone || 'Não informado'}</p>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>Entidade</label>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '1rem' }}>{selectedVolunteer.entity_name || 'Não atribuído'}</p>
                  </div>
                </div>
              </div>

              {/* Status e Datas */}
              <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '1rem', marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 1rem', fontSize: '1rem', color: '#333' }}>Status e Histórico</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>Status do Voluntário</label>
                    <div style={{ marginTop: '0.25rem' }}>{getStatusBadge(selectedVolunteer.volunteer_status)}</div>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>Registado em</label>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '1rem' }}>{formatDate(selectedVolunteer.created_at)}</p>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>Último Login</label>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '1rem' }}>{formatDate(selectedVolunteer.last_login)}</p>
                  </div>
                </div>
              </div>

              {/* Informações de Atualização */}
              {selectedVolunteer.updated_at && (
                <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '1rem', marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 1rem', fontSize: '1rem', color: '#333' }}>Última Atualização</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>Atualizado em</label>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '1rem' }}>{formatDate(selectedVolunteer.updated_at)}</p>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontWeight: 'bold', color: '#666', fontSize: '0.85rem' }}>Atualizado por</label>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '1rem', wordBreak: 'break-word' }}>
                        {selectedVolunteer.updated_by_email || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notas */}
              {selectedVolunteer.notes && (
                <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '1rem' }}>
                  <h4 style={{ margin: '0 0 1rem', fontSize: '1rem', color: '#333' }}>Notas</h4>
                  <div style={{
                    padding: '0.75rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6',
                    fontSize: '0.95rem',
                    lineHeight: '1.5'
                  }}>
                    {selectedVolunteer.notes}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 1rem 1rem' }}>
              <button className="btn btn-secondary" onClick={closeModals}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Aprovar Voluntário</h3>
              <button className="close-btn" onClick={closeModals}>×</button>
            </div>
            
            <div className="form-group">
              <label>Nome: {selectedVolunteer?.nome}</label>
            </div>
            
            <div className="form-group">
              <label>Email: {selectedVolunteer?.email}</label>
            </div>

            <div className="form-group">
              <label htmlFor="entidade_id">Entidade *</label>
              <select
                id="entidade_id"
                className="form-control"
                value={formData.entidade_id || ''}
                onChange={(e) => setFormData({...formData, entidade_id: e.target.value})}
                required
              >
                <option value="">Selecione uma entidade...</option>
                {entities && entities.map(entity => (
                  <option key={entity.id} value={entity.id}>
                    {entity.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notas</label>
              <textarea
                id="notes"
                className="form-control"
                rows="3"
                value={formData.notes || ''}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Notas sobre a aprovação..."
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between' }}>
              <button className="btn btn-secondary" onClick={closeModals}>
                Cancelar
              </button>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-danger"
                  onClick={submitRejection}
                  style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                >
                  Rejeitar
                </button>
                <button
                  className="btn btn-success"
                  onClick={submitApproval}
                >
                  Aprovar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Alterar Status do Voluntário</h3>
              <button className="close-btn" onClick={closeModals}>×</button>
            </div>
            
            <div className="form-group">
              <label>Nome: {selectedVolunteer?.nome}</label>
            </div>
            
            <div className="form-group">
              <label htmlFor="volunteer_status">Novo Status</label>
              <select 
                id="volunteer_status"
                className="form-control"
                value={formData.volunteer_status || ''}
                onChange={(e) => setFormData({...formData, volunteer_status: e.target.value})}
                required
              >
                <option value="approved">Aprovado</option>
                <option value="suspended">Suspenso</option>
                <option value="pending">Pendente</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="status_notes">Motivo/Notas</label>
              <textarea 
                id="status_notes"
                className="form-control"
                rows="3"
                value={formData.notes || ''}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Motivo da alteração de status..."
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeModals}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={submitStatusUpdate}>
                Alterar Status
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default VolunteerTable;